import { defineStore } from 'pinia'
import { ref } from 'vue'

export type AlertType = 'error' | 'warning' | 'success' | 'info'

export interface Alert {
  id: string
  type: AlertType
  message: string
  autoDismiss: boolean
  duration: number
}

export interface AlertInput {
  type: AlertType
  message: string
  autoDismiss?: boolean
  duration?: number
}

const TYPE_DEFAULTS: Record<AlertType, { autoDismiss: boolean; duration: number }> = {
  error:   { autoDismiss: false, duration: 0 },
  warning: { autoDismiss: false, duration: 0 },
  success: { autoDismiss: true,  duration: 4000 },
  info:    { autoDismiss: true,  duration: 5000 },
}

const MAX_ALERTS = 4

export const useAlertsStore = defineStore('alerts', () => {
  const alerts = ref<Alert[]>([])
  const timers = new Map<string, ReturnType<typeof setTimeout>>()

  function push(input: AlertInput): string {
    const defaults = TYPE_DEFAULTS[input.type]
    const id = `alert-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    const alert: Alert = {
      id,
      type: input.type,
      message: input.message,
      autoDismiss: input.autoDismiss ?? defaults.autoDismiss,
      duration: input.duration ?? defaults.duration,
    }

    // If at the cap, evict the oldest auto-dismiss alert to make room.
    // If all alerts are persistent, skip adding rather than hiding important errors.
    if (alerts.value.length >= MAX_ALERTS) {
      const oldestAutoDismiss = [...alerts.value].reverse().find(a => a.autoDismiss)
      if (oldestAutoDismiss) {
        dismiss(oldestAutoDismiss.id)
      } else {
        return id
      }
    }

    alerts.value.unshift(alert) // newest at top

    if (alert.autoDismiss && alert.duration > 0) {
      const timer = setTimeout(() => dismiss(id), alert.duration)
      timers.set(id, timer)
    }

    return id
  }

  function dismiss(id: string): void {
    const timer = timers.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.delete(id)
    }
    const idx = alerts.value.findIndex(a => a.id === id)
    if (idx !== -1) alerts.value.splice(idx, 1)
  }

  function clear(): void {
    timers.forEach(t => clearTimeout(t))
    timers.clear()
    alerts.value = []
  }

  return { alerts, push, dismiss, clear }
})
